import { AccommodationTable, Room, Bed } from '../../types/accommodation';
import { VisualHotel, VisualBuilding, VisualFloor, VisualRoom } from './types';

const extractFloorNumber = (roomNumber: number): number => {
  // Convert room number to string and pad with leading zeros if needed
  const roomStr = roomNumber.toString().padStart(3, '0');
  
  // For room numbers like 101, 102, etc:
  // First digit represents the floor number
  // Handle special case for ground floor (0)
  const floorNum = parseInt(roomStr[0]);
  return floorNum === 0 ? 1 : floorNum; // Convert floor 0 to 1
};

export const transformAccommodationData = (tables: AccommodationTable[]): Record<string, VisualHotel> => {
  console.log('Starting accommodation data transformation:', {
    tableCount: tables.length,
    tables: tables.map(t => ({
      id: t.i,
      hotelName: t.hn,
      roomCount: t.r.length
    }))
  });

  const hotels: Record<string, VisualHotel> = {};

  // Group tables by hotel
  const hotelGroups = tables.reduce((groups, table) => {
    if (!groups[table.hi]) {
      groups[table.hi] = [];
    }
    groups[table.hi].push(table);
    return groups;
  }, {} as Record<string, AccommodationTable[]>);

  // Transform each hotel group
  Object.entries(hotelGroups).forEach(([hotelId, hotelTables]) => {
    const buildings = hotelTables.map(table => transformTableToBuilding(table));
    
    hotels[hotelId] = {
      id: hotelId,
      name: hotelTables[0].hn,
      buildings,
      hasMultipleBuildings: buildings.length > 1
    };
  });

  return hotels;
};

const transformTableToBuilding = (table: AccommodationTable): VisualBuilding => {
  // Group rooms by floor
  const floorGroups = table.r.reduce((groups, room) => {
    const floorNumber = extractFloorNumber(room.n);
    if (!groups[floorNumber]) {
      groups[floorNumber] = [];
    }
    groups[floorNumber].push(room);
    return groups;
  }, {} as Record<number, Room[]>);

  console.log('Grouped rooms by floor:', {
    tableId: table.i,
    floorCount: Object.keys(floorGroups).length,
    floors: Object.entries(floorGroups).map(([floor, rooms]) => ({
      floor,
      roomCount: rooms.length,
      occupiedRooms: rooms.filter(r => r.o > 0).length
    }))
  });

  // Transform floors
  const floors = Object.entries(floorGroups).map(([floorNumber, rooms]) => ({
    number: parseInt(floorNumber),
    rooms: rooms.map(room => transformRoomToVisual(room))
  }));

  return {
    id: table.i,
    name: table.tn,
    floors: floors.sort((a, b) => a.number - b.number),
    totalRooms: table.r.length,
    occupiedRooms: table.r.filter(r => r.o > 0).length
  };
};

const transformRoomToVisual = (room: Room): VisualRoom => {
  // Get all assignments for each bed
  const pilgrims = room.b
    .filter(bed => bed.a && bed.a.length > 0)
    .flatMap(bed => {
      // Get all assignments for this bed
      return bed.a.map(assignment => ({
        id: assignment.pid,
        name: assignment.pn,
        checkIn: assignment.ci,
        checkOut: assignment.co,
        gender: assignment.g,
        guideName: assignment.gn,
        organizerName: assignment.on,
        age: assignment.age
      }));
    });

  console.log('Room pilgrims:', {
    roomNumber: room.n,
    pilgrimCount: pilgrims.length,
    pilgrims: pilgrims.map(p => ({
      name: p.name,
      checkIn: p.checkIn,
      checkOut: p.checkOut,
      gender: p.gender,
      guideName: p.guideName,
      organizerName: p.organizerName,
      age: p.age
    }))
  });

  return {
    number: room.n.toString(),
    type: room.t,
    occupied: room.o,
    total: room.c,
    pilgrims
  };
};